import styled from "styled-components";

const focusRing = `
  &:focus-visible {
    outline: 3px solid var(--btn-primary);
    outline-offset: 2px;
  }
`;

export const Sidebar = styled.aside`
  position: fixed;
  z-index: 1100;
  inset: 0 auto 0 0;
  width: ${({ isOpen }) => (isOpen ? "272px" : "84px")};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  background: var(--bg-card);
  color: var(--text-main);
  border-right: 1px solid var(--border-color);
  box-shadow: 8px 0 28px rgba(15, 23, 42, 0.08);
  will-change: width, transform;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 1024px) and (min-width: 769px) {
    width: 84px;
  }

  @media (max-width: 768px) {
    width: min(86vw, 320px);
    transform: translateX(${({ isOpen }) => (isOpen ? "0" : "-105%")});
    box-shadow: 16px 0 40px rgba(15, 23, 42, 0.18);
  }
`;

export const TopBar = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: 1.25rem 0.9rem 1rem;
  box-sizing: border-box;
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? "space-between" : "center")};
  gap: 0.75rem;
  min-height: 52px;
  padding: 0 0.35rem 1.5rem;
`;

export const LogoContainer = styled.div`
  position: relative;
  min-width: 0;
  flex: ${({ isOpen }) => (isOpen ? 1 : 0)};
  max-width: ${({ isOpen }) => (isOpen ? "220px" : "0")};
  display: flex;
  align-items: center;
  gap: 0.7rem;
  color: var(--text-main);
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  overflow: hidden;
  transition: flex 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;

  &::after {
    position: absolute;
    right: 0.35rem;
    bottom: 0;
    left: 0.35rem;
    height: 1px;
    background: linear-gradient(90deg, var(--btn-primary), transparent 78%);
    content: "";
    opacity: ${({ isOpen }) => (isOpen ? 0.35 : 0)};
    transition: opacity 0.25s ease;
  }

  h3 {
    margin: 0;
    overflow: hidden;
    transform: translateX(${({ isOpen }) => (isOpen ? "0" : "-8px")});
    color: var(--text-main);
    font-size: 1.2rem;
    font-weight: 850;
    letter-spacing: -0.04em;
    white-space: nowrap;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

export const BrandMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: linear-gradient(135deg, var(--btn-primary), color-mix(in srgb, var(--btn-primary) 55%, #8b5cf6));
  box-shadow: 0 6px 14px color-mix(in srgb, var(--btn-primary) 24%, transparent);
  color: #fff;
  font-size: 0.92rem;
  font-weight: 900;
  letter-spacing: -0.04em;
  transform: rotate(-4deg);
`;

export const SidebarToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: transparent;
  color: var(--text-main);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
  ${focusRing}

  &:hover {
    background: var(--bg-hover);
    color: var(--btn-primary);
    transform: translateY(-1px);
  }
`;

export const MobileToggle = styled(SidebarToggle)`
  position: fixed;
  z-index: 1000;
  top: max(0.75rem, env(safe-area-inset-top));
  left: 0.75rem;
  display: none;
  border-color: var(--border-color);
  background: var(--bg-card);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12);

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? "none" : "inline-flex")};
  }
`;

export const Backdrop = styled.button`
  position: fixed;
  z-index: 1050;
  inset: 0;
  display: none;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: rgba(15, 23, 42, 0.48);
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.24s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    display: block;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    pointer-events: ${({ isOpen }) => (isOpen ? "auto" : "none")};
  }
`;

export const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0;
  padding: 0.25rem 0.1rem 1rem;
  list-style: none;

  scrollbar-width: thin;
  scrollbar-color: var(--border-color) transparent;
`;

export const NavigationPanel = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  padding: 0.65rem 0.45rem;
  box-sizing: border-box;
  border: 1px solid color-mix(in srgb, var(--border-color) 65%, transparent);
  border-radius: 18px;
  background: color-mix(in srgb, var(--bg-main) 42%, transparent);
`;

export const NavIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${({ isActive }) => (isActive ? "var(--btn-primary)" : "transparent")};
  color: ${({ isActive }) => (isActive ? "#fff" : "inherit")};
  transition: background 0.25s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const NavItem = styled.li`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? "flex-start" : "center")};
  gap: 1rem;
  width: 100%;
  min-height: 50px;
  box-sizing: border-box;
  padding: 0.8rem ${({ isOpen }) => (isOpen ? "1rem" : "0.75rem")};
  border-radius: 12px;
  background: ${({ isActive }) => (isActive ? "color-mix(in srgb, var(--btn-primary) 12%, transparent)" : "transparent")};
  color: ${({ isActive }) => (isActive ? "var(--btn-primary)" : "var(--text-main)")};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

  svg { flex: 0 0 auto; }

  > span:not([class]) {
    overflow: hidden;
    font-size: 0.95rem;
    font-weight: ${({ isActive }) => (isActive ? 700 : 500)};
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &::before {
    position: absolute;
    top: 10px;
    bottom: 10px;
    left: 0;
    width: 3px;
    border-radius: 0 4px 4px 0;
    background: var(--btn-primary);
    content: "";
    opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  }

  &:hover {
    background: var(--bg-hover);
    color: var(--btn-primary);
    transform: translateX(2px);

    ${NavIcon} {
      background: color-mix(in srgb, var(--btn-primary) 14%, transparent);
      color: var(--btn-primary);
      transform: scale(1.05);
    }
  }

  ${focusRing}
`;

export const NotificationBadge = styled.span`
  position: absolute;
  top: 4px;
  right: ${({ isOpen }) => (isOpen ? "8px" : "4px")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 21px;
  height: 21px;
  padding: 0 5px;
  border: 2px solid var(--bg-card);
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
`;

export const UserInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ isOpen }) => (isOpen ? "stretch" : "center")};
  gap: 0.8rem;
  width: 100%;
  margin-top: auto;
  padding: 1.25rem 1rem calc(1.25rem + env(safe-area-inset-bottom));
  box-sizing: border-box;
  border-top: 1px solid var(--border-color);
`;

export const UserSummary = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? "flex-start" : "center")};
  gap: 0.75rem;
  width: 100%;
  min-height: 48px;
  padding: 0.55rem 0.65rem;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 14px;
  background: var(--bg-main);
  color: var(--text-main);

  &::after {
    position: absolute;
    z-index: 1200;
    bottom: calc(100% + 0.65rem);
    left: 0;
    max-width: 230px;
    padding: 0.55rem 0.7rem;
    border: 1px solid var(--border-color);
    border-radius: 9px;
    background: var(--bg-card);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
    color: var(--text-main);
    content: attr(data-email);
    font-size: 0.75rem;
    line-height: 1.3;
    opacity: 0;
    pointer-events: none;
    transform: translateY(4px);
    transition: opacity 0.2s ease, transform 0.2s ease;
    white-space: nowrap;
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const UserAvatar = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: linear-gradient(135deg, #007ad9 0%, #2563eb 100%);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.02em;

  svg {
    display: block;
    color: #fff;
    stroke: currentColor;
  }
`;

export const UserDetails = styled.div`
  min-width: 0;
  flex: ${({ isOpen }) => (isOpen ? 1 : 0)};
  max-width: ${({ isOpen }) => (isOpen ? "180px" : "0")};
  overflow: hidden;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: translateX(${({ isOpen }) => (isOpen ? "0" : "-6px")});
  transition: opacity 0.2s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 0.82rem;
    font-weight: 750;
  }

  small {
    margin-top: 0.15rem;
    color: var(--text-secondary);
    font-size: 0.7rem;
  }
`;

export const RoleSwitcher = styled.div`
  width: 100%;
  padding: 0.35rem;
  box-sizing: border-box;
  border: 1px solid var(--border-color);
  border-radius: 14px;
  background: var(--bg-main);

  .p-selectbutton {
    display: flex;
    width: 100%;
    gap: 0.25rem;
  }

  .p-button {
    flex: 1;
    min-height: 38px;
    padding: 0.55rem 0.5rem;
    border: 0;
    border-radius: 10px;
    font-size: 0.78rem;
    font-weight: 700;
  }
`;

export const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? "flex-start" : "center")};
  gap: 0.75rem;
  width: 100%;
  min-height: 44px;
  padding: 0.7rem 0.85rem;
  border: 0;
  border-radius: 12px;
  background: rgba(239, 68, 68, 0.08);
  color: var(--text-main);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
  ${focusRing}

  span { display: ${({ isOpen }) => (isOpen ? "inline" : "none")}; }

  &:hover {
    background: rgba(239, 68, 68, 0.14);
    color: #ef4444;
    transform: translateY(-1px);
  }
`;

export const MainContent = styled.main.withConfig({
  shouldForwardProp: (prop) => prop !== "isSidebarOpen",
})`
  min-height: 100vh;
  box-sizing: border-box;
  margin-left: ${({ isSidebarOpen }) => (isSidebarOpen ? "272px" : "84px")};
  padding: 1.5rem;
  background: var(--bg-main);
  color: var(--text-main);
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 1024px) {
    margin-left: 84px;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    padding: 4.5rem 1rem 1.5rem;
  }
`;
